// StatusBadge component for UI Component Library
// Created as part of ENG2-1691: Phase 3 - Ticket 3.1

import React from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { tokens } from '../../../styles/tokens'
import { StatusBadgeProps, StatusType, statusLabels } from './badge.types'

// Color configurations for each status
const statusColors: Record<StatusType, { bg: string; text: string; dot: string }> = {
  active: {
    bg: tokens.colors.success[100],
    text: tokens.colors.success[600],
    dot: tokens.colors.success[500],
  },
  trial: {
    bg: tokens.colors.accent[100],
    text: tokens.colors.warning[600],
    dot: tokens.colors.accent[500],
  },
  expired: {
    bg: tokens.colors.error[100],
    text: tokens.colors.error[600],
    dot: tokens.colors.error[500],
  },
  inactive: {
    bg: tokens.colors.grey[100],
    text: tokens.colors.grey[500],
    dot: tokens.colors.grey[400],
  },
}

// Styled badge container
const BadgeContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'status',
})<{ status: StatusType }>(({ status }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  borderRadius: 20,
  backgroundColor: statusColors[status].bg,
}))

// Styled dot indicator
const StatusDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'status',
})<{ status: StatusType }>(({ status }) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: statusColors[status].dot,
}))

// Styled label
const StatusLabel = styled('span', {
  shouldForwardProp: (prop) => prop !== 'status',
})<{ status: StatusType }>(({ status }) => ({
  fontSize: 12,
  fontWeight: 500,
  color: statusColors[status].text,
  lineHeight: 1,
}))

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  label,
  showDot = true,
  className,
}) => {
  const displayLabel = label ?? statusLabels[status]

  return (
    <BadgeContainer status={status} className={className}>
      {showDot && <StatusDot status={status} />}
      <StatusLabel status={status}>
        {displayLabel}
      </StatusLabel>
    </BadgeContainer>
  )
}

StatusBadge.displayName = 'StatusBadge'

export default StatusBadge
